import React from 'react';
import { Link, router } from '@inertiajs/react';
import SearchBar from '../components/Wiki/SearchBar';
import TopBar, { TBTN, TBTN_PRIMARY } from '../components/Wiki/TopBar';
import { useWikiShell } from '../components/Wiki/shell';
import WikiLayout, { DOC } from '../layouts/WikiLayout';
import useWikiNav from '../hooks/useWikiNav';
import { freshness, STALE_DAYS } from '../utils/freshness';
import type {
  WikiPageSummary,
  WikiSearchResult,
  WikiSort,
  WikiTagUsage,
} from '../types/wiki';

type WikiProps = {
  // 整个库，一次摊完（服务端 INDEX_LIMIT 封顶）。筛选时是空数组。
  pages: WikiPageSummary[];
  // 上限之外还剩多少页没列出。0 = 这一列真的是全部。
  omitted: number;
  // null = 没在筛选（显示整库那一列）；[] = 筛了但没筛到。
  results: WikiSearchResult[] | null;
  search: string;
  tag: string;
  sort: WikiSort;
  // 搜索条那一排标签候选，按挂载页数倒序、只含挂两页以上的。
  tags: WikiTagUsage[];
  // 全量有多少个（含只挂一页的）。「共 N 个」那句用它。
  totalTags: number;
};

type WikiPageComponent = React.FC<WikiProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 列表的一条。不是表格行 —— 有 hover 底色、点哪儿都能进，行与行之间没有分割线
// （改版前是 divide-y：那让一列文档看起来像一张数据表，而它们是文章）。
//
// 负 margin + 内 padding 是为了 hover 的底色能盖到正文列的边缘之外一点，
// 视觉上像「整行都可点」，而文字仍与页头对齐。
export const ITEM =
  '-mx-2.5 flex items-center gap-2.5 rounded-[7px] px-2.5 py-2.5 hover:bg-wiki-sideHover';

const Row: React.FC<{ page: WikiPageSummary; snippet?: string | null }> = ({ page, snippet }) => (
  <Link href={`/wiki/${page.url_key}`} className={ITEM}>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] text-todo-ink">{page.title}</span>
        {/* 摘录只在搜索结果里有。没有它，一列标题看不出为什么它们被搜出来。 */}
        {snippet && <span className="mt-0.5 block truncate text-[12.5px] text-todo-muted">{snippet}</span>}
      </span>

      {/* 标签是**可点的 chip**（改版前是一段 tags.join(' · ') 的灰字，点不了）。
          标签本来就是这个库唯一的横向维度，把它画成不可点的装饰是浪费。

          点它就是带着 ?tag= 回列表页 —— 与搜索条上那排 chip 同一个动作、同一个
          地址，所以两处点同一个标签得到的是同一个页面（能收藏、能后退）。

          用嵌套的 <Link> 会被 React 警告（<a> 里套 <a>），所以这里是 button +
          router.visit。preventDefault 是必须的：它长在外层那个 <Link> 里面，
          不拦就是「筛选跳了，同时整页也导航去了那篇文章」。 */}
      {page.tags.length > 0 && (
        <span className="flex shrink-0 gap-1.5">
          {page.tags.slice(0, 3).map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                router.visit(`/wiki?tag=${encodeURIComponent(tag)}`);
              }}
              className="rounded-full border border-todo-line bg-todo-card2 px-[7px] py-px
                         text-[11px] text-todo-muted hover:border-todo-line2 hover:text-todo-ink2"
            >
              {tag}
            </button>
          ))}
        </span>
      )}

      {/* 固定宽度右对齐：不定宽的话每一行的时间都从不同的位置开始，一列扫下来
          眼睛要来回找。 */}
      <span className="w-[68px] shrink-0 text-right text-[12.5px] text-todo-muted">
        {page.days_since_update > STALE_DAYS
          ? `${page.days_since_update} 天`
          : freshness(page.days_since_update, page.updated_at)}
        {page.days_since_update > STALE_DAYS && (
          // 灰点，不是琥珀点（设计稿画的是 --star）。§7.7 的原话是「不是红色、
          // 不是徽标 —— 是一个能被看见但不催人的灰点」，而琥珀在这套暖米底色上
          // 恰恰是最催人的那一档。
          <span className="ml-1 text-todo-line2" title={`${page.days_since_update} 天没动过`}>
            ●
          </span>
        )}
      </span>
    </Link>
);

const SECTION =
  'mb-1 mt-7 flex items-baseline gap-2 text-[12.5px] font-semibold tracking-wide text-todo-muted';

// 段的边界。说的是「多久没动」，不是日历月份 —— 这一列回答的问题始终是
// 「哪些还热、哪些在凉」，而不是「几月写的」。
//
// 定义在前端而不是服务端：这是**显示口径**，改一次不该动一次服务端，也不该多
// 一趟迁移。服务端只管顺序对（order(updated_at: :desc)）。
//
// 最后一段没有上界，所以整个库一定被这四段盖满 —— 不会有页面落在段与段之间
// 消失，而那是分段列表最容易出的一种 bug。
const BANDS: { name: string; sub?: string; max: number }[] = [
  { name: '本周', sub: '还热着', max: 7 },
  { name: '本月', max: 30 },
  { name: '三个月内', max: 90 },
  { name: '更早', sub: '—— 你在忘什么', max: Infinity },
];

const Wiki: WikiPageComponent = ({ pages, omitted, results, search, tag, sort, tags, totalTags }) => {
  // 建页整个归外壳（见 shell.ts 里 startNewPage 的注释）。这一页只是它的两个
  // 调用方：顶条的 ＋（不给标题）、以及下面筛选无结果时那个出口（给搜索词）。
  //
  // 这一页**不再有新建框**。改版前它在这儿：一个默认收起、由顶条 ＋ 展开的
  // 内联输入框。拿掉的理由是标题敲在一个跟它最终位置完全不像的小框里，而它
  // 的条件渲染还长出了一整类 bug（任务 #487：边栏那一行请求 focus 一个不在
  // DOM 里的节点，点了没反应）。现在标题敲在文章页那个 40px 的大标题上。
  const { focusSearch, startNewPage, newPageBusy } = useWikiShell();

  const filtering = results !== null;

  // 排序维度写进 URL（照「筛选状态从 URL 来」那条既有规矩：URL 决定内容，
  // 能收藏、能后退）。切换时把 search / tag 带上，否则一点排序筛选就没了。
  const sortHref = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (tag) params.set('tag', tag);
    // 两态切换，不做下拉：设计稿只画了一个「⇅ 按更新时间」按钮，而三个以上的
    // 排序维度在一个单人 wiki 上没有出现过需求。
    if (sort === 'updated') params.set('sort', 'title');
    const query = params.toString();
    return `/wiki${query ? `?${query}` : ''}`;
  };

  return (
    <>
      <TopBar
        actions={
          <>
            {/* 筛选中不显示排序：结果的顺序由 WikiSearchQuery 定，这个按钮点了
                不会有任何变化 —— 一个点了没反应的按钮比没有这个按钮更糟。 */}
            {!filtering && (
              <Link href={sortHref()} preserveScroll className={TBTN}>
                ⇅ {sort === 'title' ? '按标题' : '按更新时间'}
              </Link>
            )}
            {/* 包一层箭头函数，不能直接 onClick={startNewPage} —— 那会把 click
                事件当成 title 传进去，于是建出一页标题是「[object Object]」的。 */}
            <button
              type="button"
              onClick={() => startNewPage()}
              disabled={newPageBusy}
              className={TBTN_PRIMARY}
            >
              {newPageBusy ? '建页中…' : '＋ 新建页面'}
            </button>
          </>
        }
      />

      <div className={DOC}>
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <h1 className="text-[30px] font-bold tracking-tight">wiki</h1>
          {/* 「这个库有多大」。由共享 props 出（wikiNav），所以这两个数在边栏
              与这里是同一份。 */}
          <WikiStats />
        </div>

        {/* 搜索条。**这一页唯一的输入框。**
            改版前这里是「新建」和「搜索」两个几乎一模一样的整宽框上下叠着（同样
            圆角、同样边框，只有前面那个图标不同），打错的成本是白敲一行字。后来
            「新建」改成收起 + 顶条 ＋ 展开，现在整个拿掉了 —— 标题敲在文章页那个
            40px 的大标题上。于是这一页只剩一个框，不存在「敲进了错的那个」。 */}
        <SearchBar
          search={search}
          tag={tag}
          tags={tags}
          totalTags={totalTags}
          resultCount={results?.length ?? null}
        />

        {filtering ? (
          <>
            {/* 「回到全部」只在筛选时出现。搜索条自己已经显示了搜索词与选中的
                标签（各带一个 ✕），所以这里不再重复它们 —— 只补一个「两个都清」
                的出口，那是上面两个 ✕ 各自做不到的。 */}
            <div className="mb-3 text-[12.5px]">
              <Link href="/wiki" className="text-todo-muted hover:text-todo-ink2">
                ← 回到全部
              </Link>
            </div>

            {results!.length === 0 ? (
              // 搜不到的时候，人的下一个动作八成就是建这一页 —— 而标题已经在
              // 搜索框里打好了。没有这个出口就得「清筛选 → 点 ＋ → 再打一遍」。
              //
              // 只在有搜索词时给：只选了标签没打词的时候没有标题可用。
              <p className="text-[13.5px] text-todo-muted">
                没有匹配的页面。
                {search.trim() && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => startNewPage(search.trim())}
                      disabled={newPageBusy}
                      className="text-todo-accent hover:underline disabled:opacity-50"
                    >
                      {newPageBusy ? '建页中…' : `建「${search.trim()}」这一页`}
                    </button>
                  </>
                )}
              </p>
            ) : (
              <div>
                {results!.map((page) => (
                  <Row key={page.url_key} page={page} snippet={page.snippet} />
                ))}
              </div>
            )}
          </>
        ) : pages.length === 0 ? (
          // 空库时把建页摊出来：这时它是这一页唯一有意义的动作，不该只藏在顶条
          // 那个 ＋ 里。（改版前这儿是一句「上面写个标题按回车」，指的是那个已经
          // 不存在的内联框。）
          <p className="text-[13.5px] text-todo-muted">
            还没有页面。{' '}
            <button
              type="button"
              onClick={() => startNewPage()}
              disabled={newPageBusy}
              className="text-todo-accent hover:underline disabled:opacity-50"
            >
              {newPageBusy ? '建页中…' : '建第一页'}
            </button>
            ，或者让 Claude 记一件事进来。
          </p>
        ) : sort === 'title' ? (
          // 按标题排时不分段：段说的是「多久没动」，而这一档的顺序与那件事无关，
          // 硬分出来的段内顺序会是乱的。
          <>
            <div className={SECTION}>
              全部页面<span className="ml-auto font-normal tracking-normal">{pages.length}</span>
            </div>
            <div>
              {pages.map((page) => (
                <Row key={page.url_key} page={page} />
              ))}
            </div>
          </>
        ) : (
          // 整库按「多久没动」分段。改版前这里是「最近编辑 30 条 + 最久没动
          // 10 条」，71 页的库里有 31 页**没有任何入口**能走到。
          BANDS.map((band, index) => {
            const floor = index === 0 ? -1 : BANDS[index - 1].max;
            const hit = pages.filter(
              (page) => page.days_since_update > floor && page.days_since_update <= band.max,
            );
            if (hit.length === 0) return null;

            return (
              <React.Fragment key={band.name}>
                <div className={SECTION}>
                  {band.name}
                  {band.sub && <span className="font-normal tracking-normal">{band.sub}</span>}
                  <span className="ml-auto font-normal tracking-normal">{hit.length}</span>
                </div>
                <div>
                  {hit.map((page) => (
                    <Row key={page.url_key} page={page} />
                  ))}
                </div>
              </React.Fragment>
            );
          })
        )}

        {/* 摊不下的那些。只在真的有的时候才出现 —— 改版前那句「全部页面」底下
            永远只有 30 条，而它从不说自己截过。有上限不是错，说自己是全部才是。 */}
        {!filtering && omitted > 0 && (
          <p className="mt-6 text-[12.5px] text-todo-muted">
            还有 {omitted} 页没列出。用
            <button
              type="button"
              onClick={focusSearch}
              className="mx-1 underline underline-offset-2 hover:text-todo-ink2"
            >
              搜索
            </button>
            找它们。
          </p>
        )}
      </div>
    </>
  );
};

// 页数与上次写入。单独一个组件是因为它读共享 props（wikiNav），而把 usePage
// 调用塞在 Wiki 组件顶部会让人以为这两个数是 index 的 props。
//
// 「上次写入」取 nav.recent[0]：那一列按 updated_at 倒序，所以头一条就是最后
// 被写的那一页，而它带着**服务端算好的** days_since_update。
// 不用一个单独的 lastWriteAt 时间戳 —— 只有 ISO 没有天数的话，前端就得自己算
// 日历差，而那正是 days_since_update 当初存在的理由（浏览器时区与 Rails 的
// Asia/Shanghai 可能差一天，PR #7 就是这么红的）。
const WikiStats: React.FC = () => {
  const nav = useWikiNav();
  if (!nav) return null;

  const last = nav.recent[0];

  return (
    <div className="pb-[5px] text-[13.5px] text-todo-muted">
      {nav.total} 页
      {last && <> · 上次写入 {freshness(last.days_since_update, last.updated_at)}</>}
    </div>
  );
};

Wiki.layout = (page: React.ReactNode) => <WikiLayout>{page}</WikiLayout>;

export default Wiki;
